import type { CatalogEntry } from './catalog.js';
import { catalogForGroup, STANDARD_CATALOG } from './catalog.js';
import {
  getExchangeClient,
  isWholeCatalogue,
  EXCHANGE_PAGE_SIZE,
  type ExchangeClient,
  type ExchangeListing,
  type ExchangePage,
} from './exchange-catalog.js';
import { getInstallClient, type InstallClient, type InstallOutcome } from './install-client.js';
import './exchange-surface.css';

/**
 * CAOS-290 — the in-shell exchange: what "Get more apps" opens.
 *
 * ── WHAT THIS REPLACES, AND WHAT IT KEEPS ────────────────────────────────────────────────────
 * CAOS-287 put a "Get more apps" row at the foot of the App Launcher and pointed it at the
 * empty-workspace package picker — five entries from a compiled-in manifest, each with a label and
 * one line of prose, and nothing else to say. That was always a stopgap; this ticket's own text
 * calls it one.
 *
 * What makes a replacement possible now is CAOS-1682, which landed `GET packages/published`: the
 * cross-publisher registry read. For every live published version it carries the publisher's org
 * and company name, the version, when it was published, what the version CONTAINS by member kind
 * and count, what it depends on, whether installing needs a key, and whether it is still beta. That
 * is a marketplace's worth of real content, and none of it is invented.
 *
 * ── TWO PROVENANCES ON ONE SURFACE, WHICH IS THE DECISION WORTH DEFENDING ────────────────────
 * The registry is NOT a superset of the manifest. The five standard entries install through the
 * compiled-in standard registry (`kernel/src/packaging/standard-registry.generated.ts`), which is a
 * different resolver from the published-version registry — `install` versus `install-by-key`, two
 * doors with different authority, as `install-client.ts` sets out at length. An org whose published
 * registry does not happen to carry Sales Atlas would, if this surface listed only published
 * versions, stop being offered Sales Atlas the day this merged.
 *
 * So the surface lists both, in two labelled sections, and says which is which:
 *   • "Included with the platform" — {@link STANDARD_CATALOG}'s app entries, installed by package
 *     key through {@link InstallClient.install}. The incumbent behaviour, unchanged and preserved.
 *   • "Published to the exchange" — the registry read, installed by universal key through
 *     {@link InstallClient.installByKey}.
 *
 * Retiring the manifest is explicitly out of scope on the ticket, and a surface that silently
 * stopped offering four shipped apps would be a regression dressed as a feature.
 *
 * ── THE PUBLISHED SECTION DEGRADES, AND THAT IS WHAT LETS THIS LAND AT ALL ───────────────────
 * The registry route is gated on `setup.access` and needs a kernel new enough to answer it. A
 * reader without that grant, an older platform, or an unreachable one gets `ok: false` from the
 * read seam, and this surface then renders the platform section exactly as before plus a sentence
 * saying why the exchange half is empty. It is never WORSE than the popup it replaces, in any org,
 * for any reader — which is the property that means this does not have to wait on the CWEB
 * marketplace epic.
 *
 * An empty registry is a different sentence from an unreadable one, all the way down. See the
 * three-outcome note in `exchange-catalog.ts`.
 *
 * ── WHAT IS DELIBERATELY NOT HERE ────────────────────────────────────────────────────────────
 * Imagery, screenshots, categories, long descriptions, ratings. Those are absent from the data
 * model rather than from this surface: `caos.published_version` stores none of them, and authoring
 * them is a change to the package metatype, not work this surface can absorb. The ticket settles
 * this in terms — they are out of scope and not a finding.
 *
 * Upgrading an installed package to a newer listed version is also not offered, and that one IS a
 * finding rather than a scope line — see {@link isPackageInstalled}.
 */

/** What the surface needs to open. */
export interface ExchangeSurfaceDeps {
  /**
   * The package keys already installed in this org. Required, not optional.
   *
   * The picker made this optional because its absence selected first-run onboarding copy. This
   * surface has no first run: it is reached from the App Launcher, which only exists once a
   * workspace is installed. An absent set would mean "offer everything as available", and the one
   * honest reading of not knowing is not a mode worth having here.
   */
  readonly installedPackageKeys: ReadonlySet<string>;
  /** Invoked once, after a successful install, before the surface closes — the reboot seam. */
  readonly onInstalled: () => void | Promise<void>;
  /** The install boundary; defaults to the active {@link getInstallClient}. */
  readonly installClient?: InstallClient;
  /** The registry read boundary; defaults to the active {@link getExchangeClient}. */
  readonly exchangeClient?: ExchangeClient;
  /** The manifest the platform section draws from; defaults to {@link STANDARD_CATALOG}. */
  readonly catalog?: readonly CatalogEntry[];
  /** Where to mount the overlay; defaults to `document.body`. */
  readonly mountInto?: HTMLElement;
}

/** A handle on an open surface — for tests and for programmatic drive. */
export interface ExchangeSurfaceHandle {
  /** The mounted `<caos-overlay>` element. */
  readonly element: HTMLElement;
  /**
   * Settles once the first registry read has been painted.
   *
   * Exposed because the read is deliberately NOT awaited before the surface appears — the platform
   * section is on screen and usable immediately, exactly as `describeRequirements` does in the
   * picker. A caller (or a test) that wants to look at the exchange half has to have something to
   * wait on, and the alternative is polling the DOM, which is how a suite acquires a flake.
   */
  readonly ready: Promise<void>;
  /** Unmount the surface. */
  close(): void;
  /** Install one platform entry — the same path its button drives. */
  installPlatformEntry(entryId: string): Promise<InstallOutcome>;
  /** Install one published version by its universal key — the same path its button drives. */
  installListing(universalKey: string): Promise<InstallOutcome>;
  /** Narrow what is shown, as typing in the filter box does. */
  setFilter(text: string): void;
  /** Read another page of the registry, as the paging controls do. */
  showPage(offset: number): Promise<void>;
}

const COPY = {
  eyebrow: 'App Exchange',
  title: 'Add apps to your workspace',
  subtitle:
    'Everything your workspace can install, from us and from everyone publishing on the platform. Each one shows who made it, which release this is, and what it adds to your workspace before you commit to it.',
};

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

/**
 * CAOS-290 — what to call a published package on screen.
 *
 * THE REGISTRY HAS NO DISPLAY NAME. `caos.published_version` stores a `package_key` and no label,
 * and `PublishedCatalogEntry` carries none, so there is nothing to read. This is recorded on the
 * ticket as the finding it is: a surface that is meant to feel like a marketplace needs a name, and
 * supplying one is a change to the package metatype rather than something this screen can do.
 *
 * Until then, the same bargain `labelForPackage` in `package-picker.ts` already struck for the same
 * problem: take the name from a manifest entry that ships this package when one does, and otherwise
 * fall back to the key itself, which is "ugly and honest".
 *
 * IT DOES NOT PRETTIFY THE KEY, and that is the considered half. `pkg_sales_atlas` → "Sales Atlas"
 * is tempting and works for four of the five standard keys; `pkg_atlantis_ai` becomes
 * "Atlantis Ai", which is not the product's name. A rule that is wrong for some inputs puts an
 * invented name on the screen and gives nobody any way to tell which names are real — strictly
 * worse than a key, which at least cannot be mistaken for a company's own words.
 */
export function exchangeListingLabel(packageKey: string, catalog: readonly CatalogEntry[]): string {
  return catalog.find((e) => e.packages.includes(packageKey))?.label ?? packageKey;
}

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/**
 * An ISO instant as a date a person reads, or the raw string when it is not one.
 *
 * Formatted explicitly rather than through `toLocaleDateString`, which would make what this screen
 * says depend on the host's locale — and make a test of it depend on the runner's. The fallback
 * returns the input untouched: a registry value this cannot parse is still a fact, and showing it
 * raw is honest where showing "Invalid Date" is not.
 */
export function formatPublishedDate(iso: string): string {
  if (iso === '') return 'date not recorded';
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso;
  return `${at.getUTCDate()} ${MONTHS[at.getUTCMonth()]} ${at.getUTCFullYear()}`;
}

/**
 * One lookup key per kind, whatever the registry spelled it: `permissionSet`, `permission_set` and
 * `permission-set` all become `permissionset`.
 *
 * CAOS-1737 — the spelling is not ours to predict. The kernel derives a group's `kind` from the
 * member's own `type` in the published artifact (`published-catalog.ts`, `m ->> 'type'`), and every
 * one of the twenty-one member types the standard registry actually ships is camelCase —
 * `valueSet`, `rosterDetailPage`, `permissionSet`. The fixture this surface was first written
 * against used `permission_set`, which is why underscore-splitting looked like the whole job.
 */
const kindKey = (kind: string): string => kind.replace(/[\s_-]+/g, '').toLowerCase();

/**
 * CAOS-1737 — what each kind is CALLED, for a person deciding whether to install something.
 *
 * A WORD MAP AND NOT A HUMANISER, which is the correction this ticket makes. The previous version
 * split underscores and added an `s`, so a camelCase metatype went through verbatim and a card read
 * "Adds 3 items: 3 valueSets". Title-casing it instead — which `kindLabel` in `metadata-search.ts`
 * does, for an audience that asked for metadata by name — would only change the defect's spelling:
 * "Roster Detail Page" is still our internal naming, said to somebody who is shopping.
 *
 * Every word here is one the product already uses on its own screens: Object Manager, Page Layouts,
 * Value Sets, Permission Sets, App Manager, Component Library, Theme & Branding, Process Automation.
 * Nothing is invented for this sentence, because a name that appears only here is a name nobody can
 * look up.
 *
 * A KIND WITH NO WORD YET IS NOT GUESSED. It falls to {@link OTHER_MEMBER_WORD} and is counted as a
 * generic item, which is the same bargain the banned-terms gate makes everywhere else on this
 * surface: say less rather than leak an identifier. A new metatype therefore reads a little vaguely
 * the day it ships and never reads like a database column.
 */
const MEMBER_KIND_WORDS: Readonly<Record<string, { readonly one: string; readonly many: string }>> = {
  object: { one: 'object', many: 'objects' },
  field: { one: 'field', many: 'fields' },
  layout: { one: 'page layout', many: 'page layouts' },
  valueset: { one: 'value set', many: 'value sets' },
  permissionset: { one: 'permission set', many: 'permission sets' },
  app: { one: 'app', many: 'apps' },
  tab: { one: 'tab', many: 'tabs' },
  component: { one: 'component', many: 'components' },
  listview: { one: 'list view', many: 'list views' },
  page: { one: 'screen', many: 'screens' },
  rosterpage: { one: 'list screen', many: 'list screens' },
  rosterdetailpage: { one: 'record screen', many: 'record screens' },
  automation: { one: 'automation', many: 'automations' },
  validationrule: { one: 'validation rule', many: 'validation rules' },
  theme: { one: 'theme', many: 'themes' },
};

/** What a kind with no word yet is counted as. */
const OTHER_MEMBER_WORD = { one: 'other item', many: 'other items' } as const;

/** The word for a kind, or `null` when the platform has no word for it yet. */
function memberKindWords(kind: string): { readonly one: string; readonly many: string } | null {
  return MEMBER_KIND_WORDS[kindKey(kind)] ?? null;
}

function countOf(words: { readonly one: string; readonly many: string }, count: number): string {
  return `${count} ${count === 1 ? words.one : words.many}`;
}

/**
 * CAOS-290 — the sentence saying what installing this version adds.
 *
 * THREE ANSWERS, NOT TWO, which is the whole reason the kernel computes this summary at all:
 *   • the contents could not be read — `memberSummary: null`;
 *   • the version contains nothing;
 *   • it contains these kinds, in these numbers.
 *
 * And when the groups sum to less than `totalMembers`, the shortfall is STATED. The kernel excludes
 * a member it cannot describe from the groups while still counting it in the total, precisely so a
 * consumer can see that the description is short of the contents. Rendering the groups alone would
 * throw away the one fact that exclusion was designed to preserve, and the panel whose entire job
 * is to say what a package contains would quietly understate it.
 */
export function describeContents(listing: ExchangeListing): string {
  const summary = listing.memberSummary;
  if (summary === null) return 'What this adds could not be read.';
  if (summary.totalMembers === 0 && summary.groups.length === 0) return 'This version adds nothing.';

  const described = summary.groups.reduce((n, g) => n + g.count, 0);
  /*
   * CAOS-1737 — EVERY KIND WITH NO WORD YET COLLAPSES INTO ONE ENTRY, rather than each becoming its
   * own "other items". Two unnamed kinds listed separately would read "Adds 5 items: 3 other items,
   * 2 other items", which is a sentence that looks like a bug even though every number in it is
   * right. Summed, it reads "3 value sets, 2 other items" — one honest remainder.
   */
  const named = summary.groups.filter((g) => memberKindWords(g.kind) !== null);
  const unnamed = summary.groups.filter((g) => memberKindWords(g.kind) === null);
  const otherCount = unnamed.reduce((n, g) => n + g.count, 0);
  const parts = named
    .slice()
    // Largest kind first, then alphabetically, so two reads of one version say the same thing in
    // the same order — the registry sorts groups by kind, and a count-ordered list has to break its
    // own ties or the sentence moves between renders.
    .sort((a, b) => b.count - a.count || a.kind.localeCompare(b.kind))
    .map((g) => countOf(memberKindWords(g.kind)!, g.count));
  // Last, whatever its size. The remainder is the least informative part of the sentence, so it
  // reads after the parts that say something — not sorted in among them by count.
  if (otherCount > 0) parts.push(countOf(OTHER_MEMBER_WORD, otherCount));

  const head = `Adds ${summary.totalMembers} item${summary.totalMembers === 1 ? '' : 's'}`;
  const body = parts.length === 0 ? '' : `: ${parts.join(', ')}`;
  const shortfall =
    described < summary.totalMembers
      ? ` (${summary.totalMembers - described} could not be described)`
      : '';
  return `${head}${body}${shortfall}.`;
}

/** The sentence saying what this version needs from outside itself, or `''` when it needs nothing. */
export function describeDependencies(
  listing: ExchangeListing,
  catalog: readonly CatalogEntry[],
): string {
  if (listing.dependencies.length === 0) return '';
  const named = listing.dependencies.map((d) => {
    const label = exchangeListingLabel(d.package, catalog);
    return d.range === '' ? label : `${label} ${d.range}`;
  });
  const list =
    named.length === 1 ? named[0]! : `${named.slice(0, -1).join(', ')} and ${named[named.length - 1]!}`;
  return `Needs ${list}.`;
}

/** Everything a filter can match a listing on — identity, publisher, release, and contents. */
function listingHaystack(listing: ExchangeListing, catalog: readonly CatalogEntry[]): string {
  return [
    exchangeListingLabel(listing.packageKey, catalog),
    listing.packageKey,
    listing.namespace ?? '',
    listing.companyName ?? '',
    listing.version,
    ...listing.dependencies.map((d) => d.package),
    ...(listing.memberSummary?.groups ?? []).map((g) => g.kind),
  ]
    .join(' ')
    .toLowerCase();
}

/** Everything a filter can match a platform entry on. */
function entryHaystack(entry: CatalogEntry): string {
  return [entry.label, entry.summary, entry.id, ...entry.packages].join(' ').toLowerCase();
}

/** Open the in-shell exchange. Returns a handle; the surface mounts itself immediately. */
export function openExchange(deps: ExchangeSurfaceDeps): ExchangeSurfaceHandle {
  const installClient = deps.installClient ?? getInstallClient();
  const exchangeClient = deps.exchangeClient ?? getExchangeClient();
  const catalog = deps.catalog ?? STANDARD_CATALOG;
  const platformEntries = catalogForGroup('app', catalog);
  const mountInto = deps.mountInto ?? document.body;
  const installedKeys = deps.installedPackageKeys;

  /**
   * CAOS-290 — whether a package is already in this workspace.
   *
   * KEYS, NOT VERSIONS, AND THAT IS A RECORDED LIMITATION. The launcher hands this surface
   * `meta.installedPackageKeys`, a set of keys projected out of the org's metadata
   * (`metadata-source.ts`); it carries no installed version. So this surface can tell that
   * `pkg_sales_atlas` is present and cannot tell whether the 2.1.0 the registry lists is higher
   * than what is installed.
   *
   * Which is why an installed package's listed versions are shown WITHOUT an install button rather
   * than offered as an upgrade. Offering "Install" on a version whose relationship to the installed
   * one this screen cannot state would be offering an action whose effect it cannot state — and
   * guessing the direction would be worse. Upgrading from here needs the installed VERSION, which
   * means a second read (`/api/packages/list` carries one, as `setup-surface.ts` shows), and that is
   * a ticket rather than a line.
   *
   * The incumbent rule is followed exactly meanwhile: an installed thing is listed so a person can
   * SEE it is there, not hidden, and not shown as a disabled control that reads as a failure.
   */
  const isPackageInstalled = (packageKey: string): boolean => installedKeys.has(packageKey);
  const isEntryInstalled = (entry: CatalogEntry): boolean =>
    entry.packages.length > 0 && entry.packages.every(isPackageInstalled);

  const overlay = document.createElement('caos-overlay');
  overlay.setAttribute('placement', 'center');
  overlay.setAttribute('label', COPY.title);
  /*
   * THE WIDTH IS A CLASS, NOT AN INLINE STYLE, and that is a CSP requirement rather than a
   * preference.
   *
   * The ticket asks for "a much larger in-app popup", and `--caos-overlay-width` is the knob
   * <caos-overlay> documents for exactly that — the default 760px was sized for a modal holding one
   * form and this holds a browsable catalogue. The obvious way to turn it is
   * `overlay.style.setProperty(...)`.
   *
   * The host serves `style-src 'self'` with no `'unsafe-inline'` (`host/public/_headers`), and
   * `csp-compliance.test.ts` flags any `[style]` in a surface subtree. A CSSOM write is in fact
   * CSP-legal — the policy governs markup, not `el.style` — but it still REFLECTS into a style
   * attribute, so it would read as a violation to the gate and to anyone auditing the DOM, and the
   * next surface would copy it. Declared in the sheet instead: a custom property set on the host
   * element inherits into the shadow root, which is how that knob is meant to be turned.
   */
  overlay.classList.add('caos-exchange-overlay');

  const wrap = el('div', 'caos-exchange');

  // ── Head, with the filter in it ───────────────────────────────────────────────────────────
  const head = el('div', 'caos-exchange__head');
  const heading = el('div', 'caos-exchange__heading');
  heading.append(
    el('p', 'caos-exchange__eyebrow', COPY.eyebrow),
    el('h2', 'caos-exchange__title', COPY.title),
    el('p', 'caos-exchange__subtitle', COPY.subtitle),
  );
  const filterWrap = el('div', 'caos-exchange__filter');
  const filterLabel = el('label', 'caos-exchange__filter-label', 'Filter');
  filterLabel.htmlFor = 'caos-exchange-filter';
  const filterInput = el('input', 'caos-exchange__filter-input');
  filterInput.type = 'search';
  filterInput.id = 'caos-exchange-filter';
  filterInput.placeholder = 'Name, publisher, or what it adds';
  filterWrap.append(filterLabel, filterInput);
  head.append(heading, filterWrap);
  wrap.append(head);

  const status = el('div', 'caos-exchange__status');
  const body = el('div', 'caos-exchange__body');

  // ── The platform section ──────────────────────────────────────────────────────────────────
  const platformSection = el('section', 'caos-exchange__section');
  platformSection.dataset.section = 'platform';
  const platformHead = el('div', 'caos-exchange__section-head');
  platformHead.append(
    el('h3', 'caos-exchange__section-title', 'Included with the platform'),
    el(
      'p',
      'caos-exchange__section-note',
      'Built by us and shipped with your platform. These install straight away.',
    ),
  );
  const platformList = el('div', 'caos-exchange__list');
  const platformEmpty = el('p', 'caos-exchange__empty', 'Nothing here matches that filter.');
  platformEmpty.hidden = true;
  platformSection.append(platformHead, platformList, platformEmpty);

  // ── The published section ─────────────────────────────────────────────────────────────────
  const publishedSection = el('section', 'caos-exchange__section');
  publishedSection.dataset.section = 'published';
  const publishedHead = el('div', 'caos-exchange__section-head');
  const publishedCount = el('p', 'caos-exchange__section-note', 'Reading what has been published…');
  publishedCount.dataset.role = 'published-count';
  publishedHead.append(
    el('h3', 'caos-exchange__section-title', 'Published to the exchange'),
    publishedCount,
  );
  const publishedList = el('div', 'caos-exchange__list');
  const publishedNotice = el('div', 'caos-exchange__notice');
  publishedNotice.dataset.role = 'published-notice';
  publishedNotice.hidden = true;
  const paging = el('div', 'caos-exchange__paging');
  paging.hidden = true;
  publishedSection.append(publishedHead, publishedNotice, publishedList, paging);

  body.append(platformSection, publishedSection);
  wrap.append(body, status);

  /*
   * ── INSTALL STATE, AND WHY IT IS NOT A SET OF BUTTONS ───────────────────────────────────────
   *
   * The picker keeps its action buttons in an array, because its list is drawn ONCE. This list is
   * not: it is redrawn on every filter keystroke and on every page turn. A review found both halves
   * of what that costs, and they are different defects:
   *
   *   1. `renderPublished` added each new button to a Set it never cleared, so every keystroke and
   *      every page turn left a detached node in it for the life of the surface, which `setBusy`
   *      then went on iterating forever.
   *   2. Worse, and the one a person would SEE: a redraw mid-install produced fresh buttons that had
   *      never been marked disabled. So filtering while an install was in flight painted a screen
   *      full of apparently-pressable Install buttons, and pressing one did nothing at all — swallowed
   *      by the `installing` guard inside the handler. A control that does nothing when pressed is
   *      exactly the defect CAOS-299 was raised for.
   *
   * So busy-ness is held as STATE and applied to whatever buttons currently exist, rather than
   * remembered as a list of nodes. `applyBusy` is called at the end of each render, which is what
   * makes a freshly drawn button inherit an install already running instead of contradicting it.
   * The active button is identified by its CARD's key rather than by node identity, because the node
   * that was clicked may well have been replaced by the time an outcome arrives.
   */
  let installing = false;
  /*
   * CAOS-1737 — WHICH CARD IS MID-INSTALL, HELD AS A VALUE TO COMPARE AND NOT AS A SELECTOR.
   *
   * This was a selector STRING built by interpolation —
   * `` `[data-universal-key="${listing.universalKey}"]` `` — which `applyBusy` then re-parsed on
   * every call. A universal key or catalogue id containing a `"` character makes that selector
   * malformed, so `querySelector` raises SyntaxError. It raised it INSIDE the install path, before
   * the button had been marked, and the call sites are `void install…(…)`: a person pressing Install
   * got no busy state, no progress line and no error. A button that does nothing when pressed, and
   * the only trace an unhandled rejection. These values come from the published registry, which is
   * to say from a publisher, so they are not ours to assume are tame.
   *
   * CSS.escape IS NOT THE FIX, which is worth recording because it is the obvious one and it was
   * tried and measured. It escapes for an identifier while these values sit inside a quoted string,
   * so it emits forms that are legal per spec but that the selector engine here will not match:
   * `1abc` becomes `\31 abc`, and a backslash doubles. A key holding a backslash or a leading digit
   * MATCHES unescaped and STOPS matching once escaped — so escaping trades one broken case for
   * another, and the trade cannot even be tested.
   *
   * So the class of bug goes rather than the character: the card's own `dataset` value is compared,
   * and nothing is ever reparsed. Any key works, including one containing `"`, a backslash or a
   * bracket, because no key is ever part of a query again.
   */
  let activeCard: { readonly field: 'entryId' | 'universalKey'; readonly value: string } | null = null;

  /** Is this action button the one whose install is in flight? Read off its card, never queried. */
  const isActiveAction = (action: HTMLElement): boolean => {
    if (activeCard === null) return false;
    const card = action.closest<HTMLElement>('.caos-exchange__card');
    return card !== null && card.dataset[activeCard.field] === activeCard.value;
  };

  /** Mark every action button in the surface for the install state as it currently stands. */
  const applyBusy = (): void => {
    for (const b of wrap.querySelectorAll<HTMLElement>('.caos-exchange__action')) {
      if (installing) b.setAttribute('disabled', '');
      else b.removeAttribute('disabled');
      if (installing && isActiveAction(b)) b.setAttribute('loading', '');
      else b.removeAttribute('loading');
    }
    /*
     * The paging controls are governed from here too, and they need their own line because their
     * enabled state has a SECOND reason in it: whether there is a page that way at all. `installing`
     * alone cannot answer that, so each button records its own navigability when it is built and
     * this re-reads it rather than guessing.
     *
     * Without this the controls would latch: an install that fails or no-ops leaves the surface
     * open, and the buttons disabled on the way in would never be re-enabled on the way out,
     * because nothing redraws the list on either of those outcomes.
     */
    for (const b of paging.querySelectorAll<HTMLElement>('caos-button')) {
      if (b.dataset.navigable === 'true' && !installing) b.removeAttribute('disabled');
      else b.setAttribute('disabled', '');
    }
  };

  const setBusy = (busy: boolean, card?: { field: 'entryId' | 'universalKey'; value: string }): void => {
    installing = busy;
    activeCard = busy ? (card ?? null) : null;
    applyBusy();
  };

  const clearStatus = (): void => status.replaceChildren();

  const showProgress = (label: string): void => {
    clearStatus();
    const bar = document.createElement('caos-progress-bar');
    bar.setAttribute('indeterminate', '');
    bar.setAttribute('label', label);
    status.append(bar);
  };

  const banner = (intent: string, title: string, text: string): void => {
    clearStatus();
    const node = document.createElement('caos-notice-banner');
    node.setAttribute('intent', intent);
    node.setAttribute('title', title);
    node.textContent = text;
    status.append(node);
  };

  const close = (): void => {
    overlay.remove();
  };

  /**
   * The one place an install outcome becomes something on screen, shared by both sections.
   *
   * The two doors differ only in which client call they make; everything after the outcome is
   * identical, and the picker's history is the argument for not writing it twice — CAOS-1715 found
   * the no-op reporting had to be fixed in a loop that had kept only the last outcome, and a second
   * copy of this is exactly the kind of thing that gets fixed in one place and not the other.
   *
   * A NO-OP DOES NOT REBOOT AND DOES NOT CLOSE, following CAOS-1715's settled shape: the surface
   * stays up holding the one sentence that tells "already installed" from "installed", because a
   * banner painted microseconds before `overlay.remove()` is a message nobody reads.
   */
  const finishInstall = async (label: string, outcome: InstallOutcome): Promise<InstallOutcome> => {
    setBusy(false);
    if (!outcome.ok) {
      banner('danger', 'Install failed', outcome.error ?? 'The app could not be installed.');
      return outcome;
    }
    if (outcome.noop === true) {
      banner('info', 'Already installed', `${label} was already installed. Nothing changed.`);
      return outcome;
    }
    banner('success', 'Installed', `${label} installed. Bringing your workspace up to date…`);
    await Promise.resolve(deps.onInstalled());
    close();
    return outcome;
  };

  const installPlatformEntry = async (entryId: string): Promise<InstallOutcome> => {
    const entry = platformEntries.find((e) => e.id === entryId);
    if (!entry) return { ok: false, error: `Unknown catalog entry "${entryId}".` };
    // Refused here and not only by withholding the button, because the handle is a programmatic
    // entry point: a caller driving it directly would otherwise re-run an install this surface
    // deliberately does not offer. The same reasoning CAOS-287 recorded in the picker.
    if (isEntryInstalled(entry)) return { ok: true, noop: true };
    if (installing) return { ok: false, error: 'An install is already in progress.' };

    setBusy(true, { field: 'entryId', value: entry.id });

    let outcome: InstallOutcome = { ok: true };
    // AN ENTRY IS A NO-OP ONLY IF EVERY PACKAGE IN IT NO-OPPED — CAOS-1715's finding, which this
    // surface inherits because an entry can ship more than one package. Starts undefined so an
    // entry shipping no packages cannot report a no-op it never established.
    let everyPackageNoop: boolean | undefined;
    for (const pkg of entry.packages) {
      showProgress(`Installing ${entry.label}…`);
      const universalKey = entry.universalKeys?.[pkg];
      if (universalKey === undefined) {
        outcome = await installClient.install(pkg);
      } else if (installClient.installByKey === undefined) {
        outcome = {
          ok: false,
          code: 'install.by_key_unsupported',
          error: `${entry.label} installs by universal key, which this install client cannot do.`,
        };
      } else {
        outcome = await installClient.installByKey(universalKey, pkg);
      }
      if (!outcome.ok) break;
      everyPackageNoop = (everyPackageNoop ?? true) && outcome.noop === true;
    }
    if (!outcome.ok) return finishInstall(entry.label, outcome);
    return finishInstall(entry.label, { ...outcome, noop: everyPackageNoop === true });
  };

  /** Every listing currently held, so the handle can install one by key without a re-read. */
  let listings: readonly ExchangeListing[] = [];
  let page: ExchangePage | null = null;

  const installListing = async (universalKey: string): Promise<InstallOutcome> => {
    const listing = listings.find((l) => l.universalKey === universalKey);
    if (!listing) return { ok: false, error: `Unknown published version "${universalKey}".` };
    const label = exchangeListingLabel(listing.packageKey, catalog);
    if (isPackageInstalled(listing.packageKey)) return { ok: true, noop: true };
    /*
     * A PROTECTED VERSION IS REFUSED HERE, NOT ATTEMPTED.
     *
     * `protection: 'protected'` means installing needs a live secret key as well as the universal
     * one, and `httpInstallClient.installByKey` sends no secret — deliberately, as its own docblock
     * records, because the three proving fixtures are all public and an always-undefined parameter
     * is indistinguishable from one being dropped on the way.
     *
     * So an install driven from this surface CANNOT succeed for a protected version. The row says
     * so and offers no button; this is the same refusal where it actually binds, so the handle
     * cannot reach past the surface and produce a failure that reads as the platform's fault.
     */
    if (listing.protection === 'protected') {
      return {
        ok: false,
        code: 'install.key_required',
        error: `${label} ${listing.version} needs an install key, which cannot be supplied from this screen.`,
      };
    }
    if (installing) return { ok: false, error: 'An install is already in progress.' };
    if (installClient.installByKey === undefined) {
      return {
        ok: false,
        code: 'install.by_key_unsupported',
        error: `${label} installs by universal key, which this install client cannot do.`,
      };
    }

    setBusy(true, { field: 'universalKey', value: listing.universalKey });
    showProgress(`Installing ${label} ${listing.version}…`);
    const outcome = await installClient.installByKey(listing.universalKey, listing.packageKey);
    return finishInstall(`${label} ${listing.version}`, outcome);
  };

  // ── Rendering ─────────────────────────────────────────────────────────────────────────────
  let filterText = '';

  const badge = (text: string, tone: string): HTMLElement => {
    const node = el('span', 'caos-exchange__badge', text);
    node.dataset.tone = tone;
    return node;
  };

  const renderPlatform = (): void => {
    platformList.replaceChildren();
    let shown = 0;
    for (const entry of platformEntries) {
      if (filterText !== '' && !entryHaystack(entry).includes(filterText)) continue;
      shown += 1;
      const card = el('div', 'caos-exchange__card');
      card.dataset.entryId = entry.id;
      card.dataset.featured = String(Boolean(entry.featured));

      const main = el('div', 'caos-exchange__card-main');
      main.append(el('p', 'caos-exchange__card-name', entry.label));
      const badges = el('div', 'caos-exchange__badges');
      badges.append(badge('Included', 'platform'));
      if (isEntryInstalled(entry)) badges.append(badge('Installed', 'installed'));
      main.append(badges, el('p', 'caos-exchange__card-summary', entry.summary));

      card.append(main);
      if (isEntryInstalled(entry)) {
        card.dataset.installed = 'true';
      } else {
        const action = document.createElement('caos-button');
        action.className = 'caos-exchange__action';
        action.setAttribute('variant', entry.featured ? 'primary' : 'secondary');
        action.setAttribute('size', 'sm');
        action.textContent = 'Install';
        action.addEventListener('click', () => {
          if (installing) return;
          void installPlatformEntry(entry.id);
        });
        card.append(action);
      }
      platformList.append(card);
    }
    platformEmpty.hidden = shown > 0;
    /*
     * Freshly drawn buttons inherit an install already in flight. Without this, filtering
     * mid-install paints pressable-looking controls that do nothing — see the install-state note.
     *
     * IT IS NOT REDUNDANT WITH `renderPublished`'s CALL, though it looks it, and a mutation run is
     * how that was settled rather than by reading: deleting this line left all 36 tests green.
     * `renderPublished` returns EARLY when the registry read failed (`page === null`) and never
     * reaches its own `applyBusy`, so on the degraded path — which is the ordinary path for a
     * reader without `setup.access` — this is the only call there is. The suite now has a case for
     * exactly that combination, which is what makes the line defended rather than merely present.
     */
    applyBusy();
  };

  const renderListingCard = (listing: ExchangeListing): HTMLElement => {
    const label = exchangeListingLabel(listing.packageKey, catalog);
    const installed = isPackageInstalled(listing.packageKey);
    const card = el('div', 'caos-exchange__card');
    card.dataset.universalKey = listing.universalKey;
    card.dataset.packageKey = listing.packageKey;
    if (installed) card.dataset.installed = 'true';

    const main = el('div', 'caos-exchange__card-main');
    main.append(el('p', 'caos-exchange__card-name', label));

    const badges = el('div', 'caos-exchange__badges');
    badges.append(badge(listing.version, 'version'));
    // Null `promotedAt` means beta, which the registry projects and never filters. A beta version
    // installs by key only into a trial or sandbox org, so saying so before somebody presses the
    // button is the difference between a refusal they expected and one that looks like a fault.
    if (listing.promotedAt === null) badges.append(badge('Beta', 'beta'));
    if (listing.protection === 'protected') badges.append(badge('Key required', 'protected'));
    if (installed) badges.append(badge('Installed', 'installed'));
    main.append(badges);

    // The key is shown rather than hidden behind the label: it is the identity of the thing being
    // installed, the label may BE the key when no manifest entry names it, and an administrator
    // choosing between two publishers' packages needs to see which is which.
    const identity = listing.namespace === null ? listing.packageKey : `${listing.packageKey} · ${listing.namespace}`;
    main.append(el('p', 'caos-exchange__card-identity', identity));

    const publisher =
      listing.companyName === null
        ? `Publisher not registered · published ${formatPublishedDate(listing.createdAt)}`
        : `${listing.companyName} · published ${formatPublishedDate(listing.createdAt)}`;
    main.append(el('p', 'caos-exchange__card-publisher', publisher));
    main.append(el('p', 'caos-exchange__card-contents', describeContents(listing)));

    const dependencySentence = describeDependencies(listing, catalog);
    if (dependencySentence !== '') {
      main.append(el('p', 'caos-exchange__card-deps', dependencySentence));
    }

    if (listing.protection === 'protected') {
      main.append(
        el(
          'p',
          'caos-exchange__card-gate',
          'Installing this needs a key from its publisher, which cannot be supplied from this screen.',
        ),
      );
    }

    card.append(main);
    // No button for an installed package (keys, not versions — see isPackageInstalled) and none for
    // a protected one (no key to send — see installListing). Both are listed, neither is actionable.
    if (!installed && listing.protection !== 'protected') {
      /*
       * THE BUTTON CARRIES NO IDENTITY OF ITS OWN, and that is deliberate rather than an omission.
       *
       * It held a copy of `data-universal-key`, which nothing read — the busy-state lookup finds it
       * through its CARD (`[data-universal-key="…"] .caos-exchange__action`) — and the copy made
       * `[data-universal-key]` match two nodes per listing. A test of mine counted cards with that
       * selector and got double, which is the cheap version of the same mistake a later query would
       * make expensively. One node owns the identity; the action is found through it.
       */
      const action = document.createElement('caos-button');
      action.className = 'caos-exchange__action';
      action.setAttribute('variant', 'secondary');
      action.setAttribute('size', 'sm');
      action.textContent = 'Install';
      action.addEventListener('click', () => {
        if (installing) return;
        void installListing(listing.universalKey);
      });
      card.append(action);
    }
    return card;
  };

  /**
   * The count line, which has to be honest about WHAT it counted.
   *
   * A filter applied to the whole catalogue narrows the catalogue. Applied to one page of several it
   * narrows the page, and a line reading "2 apps" in that case would tell somebody an app is not
   * published when it is on page two. So the wording turns on {@link isWholeCatalogue}, and the
   * paging controls appear only when there is more than one page to move between.
   */
  const renderPublishedCount = (visible: number): void => {
    if (page === null) return;
    const whole = isWholeCatalogue(page);
    const packages = new Set(listings.map((l) => l.packageKey)).size;
    const versions = `${page.total} version${page.total === 1 ? '' : 's'}`;
    /*
     * A row the reader had to drop is SAID, not absorbed. The kernel excludes a member it cannot
     * describe from a summary while still counting it in the total, precisely so the shortfall is
     * visible; the same reasoning applies a level up to a row it could not read at all. Silence
     * here would make a short list indistinguishable from a complete one.
     */
    const unread =
      page.dropped > 0 ? ` ${page.dropped} could not be read.` : '';

    if (filterText === '') {
      if (listings.length === 0) {
        // Not "nothing matches" — nothing was READABLE. The `1–0` range this used to print came
        // from treating the two as one case.
        publishedCount.textContent = `None of the versions on this page could be read.${
          whole ? '' : ` ${versions} in all.`
        }`;
        return;
      }
      publishedCount.textContent = whole
        ? `${versions} across ${packages} package${packages === 1 ? '' : 's'}.${unread}`
        : `Showing ${page.offset + 1}–${page.offset + listings.length} of ${versions}.${unread}`;
      return;
    }
    publishedCount.textContent = whole
      ? `${visible} of ${versions} match that filter.${unread}`
      : `${visible} of the ${listings.length} on this page match that filter — ${versions} in all.${unread}`;
  };

  const renderPublished = (): void => {
    publishedList.replaceChildren();
    if (page === null) return;
    let visible = 0;
    for (const listing of listings) {
      if (filterText !== '' && !listingHaystack(listing, catalog).includes(filterText)) continue;
      visible += 1;
      publishedList.append(renderListingCard(listing));
    }
    renderPublishedCount(visible);

    if (page.total === 0) {
      publishedCount.textContent = 'Nothing has been published to the exchange yet.';
    } else if (listings.length === 0) {
      // Every row on this page was unreadable. A DIFFERENT sentence from the filter one below,
      // because it is a different fact and the count line above already says so: a reader told
      // "nothing matches that filter" with an empty filter box would reasonably conclude the
      // registry was empty.
      publishedList.append(
        el('p', 'caos-exchange__empty', 'The versions on this page could not be read.'),
      );
    } else if (visible === 0) {
      publishedList.append(el('p', 'caos-exchange__empty', 'Nothing here matches that filter.'));
    }

    /*
     * Paging shown only when there is somewhere to page to.
     *
     * `hasNext` is computed from the WINDOW (`offset + pageSize`), not from `listings.length`. It
     * was the latter, and a review's finding about dropped rows is what exposed the consequence:
     * with `total: 4`, a 200-row window and one unreadable row, `0 + 3 < 4` claimed another page
     * existed, and pressing Next read offset 200 and stranded the reader on an empty page. How many
     * rows this reader could parse is not evidence about how many rows the registry holds.
     *
     * `limit` is the applied page size the route echoed rather than what was asked for, so the
     * arithmetic matches what the platform actually did — it clamps to 200 and enforces that itself.
     */
    const pageSize = page.limit > 0 ? page.limit : EXCHANGE_PAGE_SIZE;
    const hasPrev = page.offset > 0;
    const hasNext = page.offset + pageSize < page.total;
    paging.hidden = !hasPrev && !hasNext;
    if (!paging.hidden) {
      paging.replaceChildren();
      /*
       * BOTH CONTROLS ARE DISABLED WHILE AN INSTALL IS IN FLIGHT, which a review's finding is the
       * reason for. Turning the page replaces the whole list, so the row being installed — and the
       * progress the status region is reporting about it — would vanish from under the person who
       * pressed Install. The overlay's own close handler already refuses mid-install for the same
       * reason, so this follows the incumbent rather than inventing a rule.
       *
       * DISABLED rather than withheld: a control that disappears while something is happening
       * leaves nothing to explain itself, and this one comes back the moment the install resolves.
       */
      const pageButton = (
        className: string,
        text: string,
        enabled: boolean,
        target: () => number,
      ): HTMLElement => {
        const button = document.createElement('caos-button');
        button.setAttribute('variant', 'ghost');
        button.setAttribute('size', 'sm');
        button.className = className;
        button.textContent = text;
        // Recorded on the node so `applyBusy` can re-enable it without knowing the page arithmetic.
        button.dataset.navigable = String(enabled);
        if (!enabled || installing) button.setAttribute('disabled', '');
        button.addEventListener('click', () => {
          if (!enabled || installing) return;
          void showPage(target());
        });
        return button;
      };
      paging.append(
        pageButton('caos-exchange__page-prev', 'Previous', hasPrev, () =>
          Math.max(0, page!.offset - pageSize),
        ),
        pageButton('caos-exchange__page-next', 'Next', hasNext, () => page!.offset + pageSize),
      );
    }
    // Same reason as in `renderPlatform`: every button this render just built inherits the install
    // state rather than contradicting it.
    applyBusy();
  };

  /**
   * Read a page of the registry and paint it.
   *
   * A FAILED READ LEAVES THE PLATFORM SECTION ALONE. That is the whole degradation story: the
   * notice says why the exchange half is empty, the apps that ship with the platform are still
   * listed and still installable, and the surface is never worse than the popup it replaces.
   */
  const showPage = async (offset: number): Promise<void> => {
    const outcome = await exchangeClient.published({ limit: EXCHANGE_PAGE_SIZE, offset });
    if (!outcome.ok || outcome.page === undefined) {
      page = null;
      listings = [];
      publishedList.replaceChildren();
      paging.hidden = true;
      publishedCount.textContent = 'What else has been published could not be read.';
      publishedNotice.hidden = false;
      publishedNotice.textContent =
        outcome.error ?? 'What has been published could not be read right now.';
      publishedNotice.dataset.code = outcome.code ?? '';
      return;
    }
    publishedNotice.hidden = true;
    page = outcome.page;
    listings = outcome.page.listings;
    renderPublished();
  };

  const setFilter = (text: string): void => {
    filterText = text.trim().toLowerCase();
    if (filterInput.value !== text) filterInput.value = text;
    renderPlatform();
    renderPublished();
  };

  renderPlatform();

  filterInput.addEventListener('input', () => {
    setFilter(filterInput.value);
  });

  // ── Foot ──────────────────────────────────────────────────────────────────────────────────
  const foot = el('div', 'caos-exchange__foot');
  const done = document.createElement('caos-button');
  done.setAttribute('variant', 'ghost');
  done.setAttribute('size', 'sm');
  done.textContent = 'Close';
  done.addEventListener('click', () => {
    if (!installing) close();
  });
  foot.append(done);
  wrap.append(foot);

  overlay.append(wrap);
  overlay.addEventListener('caos-overlay-close', (e) => {
    if (installing) {
      e.preventDefault();
      return;
    }
    close();
  });

  mountInto.append(overlay);

  // Deliberately not awaited before the surface appears: the platform section is already on screen
  // and usable, and a catalogue that explains itself a moment later beats one that stalls behind a
  // round trip. `ready` is what a caller waits on — see its note on the handle.
  const ready = showPage(0);

  return { element: overlay, ready, close, installPlatformEntry, installListing, setFilter, showPage };
}
